import { useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/EmptyState';
import { appUrls } from '@/lib/appUrls';
import { copyText } from '@/lib/copyText';
import type { App } from '@/types/app';

const MASK = '••••••••';
const linkClass =
  'rounded-sm text-primary underline-offset-4 hover:underline outline-none focus-visible:ring-2 focus-visible:ring-ring';

const TLS_LABELS: Record<string, string> = {
  letsencrypt: "Let's Encrypt",
  origin: 'Cloudflare origin certificate',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card className="gap-3 p-4">
      <h2 className="text-base font-semibold">{title}</h2>
      {children}
    </Card>
  );
}

export function ConfigTab({ app }: { app: App }) {
  const own = app.env_vars ?? {};
  const fromStack = Object.keys(own).length === 0;
  const env = fromStack ? (app.stack?.env_vars ?? {}) : own;
  const keys = Object.keys(env).sort((a, b) => a.localeCompare(b));

  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const allRevealed = keys.length > 0 && keys.every((k) => revealed[k]);

  const toggleAll = () => setRevealed(allRevealed ? {} : Object.fromEntries(keys.map((k) => [k, true])));
  const toggle = (k: string) => setRevealed((r) => ({ ...r, [k]: !r[k] }));

  const ports = Object.entries(app.allocated_ports ?? {});
  const urls = appUrls(app);

  return (
    <div className="space-y-4">
      <Section title="Environment variables">
        {keys.length === 0 ? (
          <EmptyState as="h3" title="No environment variables" className="border-0 py-6" />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Values are hidden by default.
              </p>
              <Button variant="outline" size="sm" onClick={toggleAll}>
                {allRevealed ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
                {allRevealed ? 'Hide all' : 'Reveal all'}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr className="border-b">
                    <th scope="col" className="py-2 pr-3 font-medium">Key</th>
                    <th scope="col" className="py-2 pr-3 font-medium">Value</th>
                    <th scope="col" className="py-2 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => {
                    const shown = !!revealed[k];
                    return (
                      <tr key={k} className="border-b last:border-0 align-top">
                        <th scope="row" className="py-2 pr-3 font-mono text-xs font-medium break-all">
                          {k}
                          {fromStack && <span className="ml-2 font-sans font-normal text-muted-foreground">(stack default)</span>}
                        </th>
                        <td className="py-2 pr-3 font-mono text-xs break-all">
                          {shown ? (
                            env[k]
                          ) : (
                            <>
                              <span aria-hidden>{MASK}</span>
                              <span className="sr-only">Value hidden</span>
                            </>
                          )}
                        </td>
                        <td className="py-2 text-right whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-pressed={shown}
                            aria-label={`Show value of ${k}`}
                            onClick={() => toggle(k)}
                          >
                            {shown ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Copy value of ${k}`}
                            onClick={() => void copyText(env[k])}
                          >
                            <Copy className="size-4" aria-hidden />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Section>

      <Section title="Ports">
        {ports.length === 0 ? (
          <p className="text-sm text-muted-foreground">No ports allocated</p>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {ports.map(([name, port]) => (
              <div key={name} className="contents">
                <dt className="font-mono text-xs text-muted-foreground">{name}</dt>
                <dd className="tabular-nums">{port}</dd>
              </div>
            ))}
          </dl>
        )}
      </Section>

      <Section title="Domains & DNS">
        {urls.length === 0 ? (
          <>
            <p className="text-sm text-muted-foreground">No subdomain assigned</p>
            {app.dns_record_id && (
              <p className="text-sm">
                <span className="text-muted-foreground">Main DNS record ID </span>
                <span className="font-mono text-xs break-all">{app.dns_record_id}</span>
              </p>
            )}
          </>
        ) : (
          <ul className="space-y-2 text-sm">
            {urls.map((u) => {
              const recordId = u.recordId ?? (u.label === 'Main' ? app.dns_record_id : undefined);
              return (
                <li key={u.label} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="w-20 shrink-0 text-muted-foreground">{u.label}</span>
                  <a href={u.href} target="_blank" rel="noopener noreferrer" className={`${linkClass} break-all`}>
                    {u.host}
                  </a>
                  {recordId && (
                    <span className="font-mono text-xs text-muted-foreground break-all">
                      <span className="sr-only">DNS record </span>
                      {recordId}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title="TLS">
        <div>
          <span className="inline-flex h-5 items-center rounded-full bg-status-neutral-soft px-2 text-xs font-medium text-status-neutral">
            {(app.tls_mode && TLS_LABELS[app.tls_mode]) ?? '—'}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">The TLS mode is fixed when the app is created and does not change afterwards.</p>
      </Section>
    </div>
  );
}
